import React, {useEffect, useMemo, useState} from 'react';
import {ServerIntf} from './ServerIntf';
import './ConfigPage.css';

const defaultNonEgmRegex = '(?i)^\\S{4}::\\d{2}$';
const defaultEgmRegex = '(?i)^[a-z0-9]{2}:[a-z0-9]{2}:[0-9]+$';
const defaultMiaiEmailRegex = '(?i)@(grabbagbingo\\.com|miai-inc\\.com)$';

const toForm = (cfg) => ({
    OpMode: String(cfg.OpMode ?? 0),
    BingoGameMS: String(cfg.BingoGameMS ?? 1000),
    ApiSource: cfg.ApiSource ?? '/api',
    TokenDurationMS: String(cfg.TokenDurationMS ?? 900000),
    PasswdDurationDays: String(cfg.PasswdDurationDays ?? 90),
    NonEgmEssRegex: cfg.NonEgmEssRegex ?? defaultNonEgmRegex,
    EgmEssRegex: cfg.EgmEssRegex ?? defaultEgmRegex,
    MiaiEmailRegex: cfg.MiaiEmailRegex ?? defaultMiaiEmailRegex,
    SlotTopic: cfg.SlotTopic ?? '',
    ssl: !!cfg.ssl
});

const editablePayload = (form) => ({
    OpMode: Number.parseInt(form.OpMode, 10),
    BingoGameMS: Number.parseInt(form.BingoGameMS, 10),
    ApiSource: form.ApiSource,
    TokenDurationMS: Number.parseInt(form.TokenDurationMS, 10),
    PasswdDurationDays: Number.parseInt(form.PasswdDurationDays, 10),
    NonEgmEssRegex: form.NonEgmEssRegex,
    EgmEssRegex: form.EgmEssRegex,
    MiaiEmailRegex: form.MiaiEmailRegex,
    SlotTopic: form.SlotTopic,
    ssl: !!form.ssl
});

const payloadError = (payload) => {
    if (
        Number.isNaN(payload.OpMode) ||
        Number.isNaN(payload.BingoGameMS) ||
        Number.isNaN(payload.TokenDurationMS) ||
        Number.isNaN(payload.PasswdDurationDays)
    ) {
        return 'OpMode, BingoGameMS, TokenDurationMS, and PasswdDurationDays must be valid numbers';
    }
    if (!payload.NonEgmEssRegex || !payload.NonEgmEssRegex.trim() || !payload.EgmEssRegex || !payload.EgmEssRegex.trim()) {
        return 'Regex fields must be non-empty';
    }
    return '';
};

const sameForm = (a, b) => JSON.stringify(editablePayload(a)) === JSON.stringify(editablePayload(b));
const redirectToDashboard = () => {
    window.history.replaceState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
};

const toLocalDatetimeInput = (ms) => {
    if (!ms || ms <= 0) {
        return '';
    }
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalDatetimeInput = (v) => {
    if (!v) {
        return 0;
    }
    const ms = new Date(v).getTime();
    if (Number.isNaN(ms)) {
        return 0;
    }
    return ms;
};

const formatCountdown = (targetMS, nowMS) => {
    if (!targetMS || targetMS <= 0) {
        return 'N/A';
    }
    let diff = Math.max(0, Math.floor((targetMS - nowMS) / 1000));
    const days = Math.floor(diff / 86400);
    diff -= days * 86400;
    const hours = Math.floor(diff / 3600);
    diff -= hours * 3600;
    const mins = Math.floor(diff / 60);
    const secs = diff - mins * 60;
    return `${days}d ${hours}h ${mins}m ${secs}s`;
};

const renderConfigFields = (prefix, form, onChange) => (
    <div className='config-grid'>
        <label className='config-label' htmlFor={`${prefix}-opmode`}>OpMode</label>
        <select id={`${prefix}-opmode`} value={form.OpMode} onChange={(e) => onChange('OpMode', e.target.value)} className='form-control'>
            <option value='0'>0 (DEBUG)</option>
            <option value='1'>1 (PROFILE)</option>
            <option value='2'>2 (STRICT)</option>
        </select>

        <label className='config-label' htmlFor={`${prefix}-bingo-ms`}>BingoGameMS</label>
        <input id={`${prefix}-bingo-ms`} type='number' className='form-control' value={form.BingoGameMS}
               onChange={(e) => onChange('BingoGameMS', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-api-source`}>ApiSource</label>
        <input id={`${prefix}-api-source`} type='text' className='form-control' value={form.ApiSource}
               onChange={(e) => onChange('ApiSource', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-token-ms`}>TokenDurationMS</label>
        <input id={`${prefix}-token-ms`} type='number' className='form-control' value={form.TokenDurationMS}
               onChange={(e) => onChange('TokenDurationMS', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-passwd-days`}>PasswdDurationDays</label>
        <input id={`${prefix}-passwd-days`} type='number' className='form-control' value={form.PasswdDurationDays}
               onChange={(e) => onChange('PasswdDurationDays', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-non-egm-ess-regex`}>NonEgmEssRegex</label>
        <input id={`${prefix}-non-egm-ess-regex`} type='text' className='form-control' value={form.NonEgmEssRegex}
               onChange={(e) => onChange('NonEgmEssRegex', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-egm-ess-regex`}>EgmEssRegex</label>
        <input id={`${prefix}-egm-ess-regex`} type='text' className='form-control' value={form.EgmEssRegex}
               onChange={(e) => onChange('EgmEssRegex', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-miai-email-regex`}>MiaiEmailRegex</label>
        <input id={`${prefix}-miai-email-regex`} type='text' className='form-control' value={form.MiaiEmailRegex}
               onChange={(e) => onChange('MiaiEmailRegex', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-slot-topic`}>SlotTopic</label>
        <input id={`${prefix}-slot-topic`} type='text' className='form-control' value={form.SlotTopic}
               onChange={(e) => onChange('SlotTopic', e.target.value)}/>

        <label className='config-label' htmlFor={`${prefix}-ssl`}>ssl</label>
        <label className='ssl-toggle' htmlFor={`${prefix}-ssl`}>
            <input id={`${prefix}-ssl`} type='checkbox' checked={form.ssl}
                   onChange={(e) => onChange('ssl', e.target.checked)}/>
            <span>{form.ssl ? 'Enabled' : 'Disabled'}</span>
        </label>
    </div>
);

export default function ConfigPage() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState(null);
    const [stagedCfg, setStagedCfg] = useState(null);
    const [stagedForm, setStagedForm] = useState(null);
    const [stagedRestartAtLocal, setStagedRestartAtLocal] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [nowMS, setNowMS] = useState(Date.now());

    useEffect(() => {
        const t = window.setInterval(() => setNowMS(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([ServerIntf.GetConfig(), ServerIntf.GetStagedConfig()])
            .then(([active, staged]) => {
                setCfg(active);
                setForm(toForm(active));
                if (staged) {
                    setStagedCfg(staged);
                    setStagedForm(toForm(staged));
                    setStagedRestartAtLocal(toLocalDatetimeInput(staged.RestartAtMS));
                } else {
                    setStagedCfg(null);
                    setStagedForm(null);
                    setStagedRestartAtLocal('');
                }
            })
            .catch((err) => {
                if (err && err.response && err.response.status === 403) {
                    ServerIntf.Warning('Access Denied', 'Only MIAI_ADMIN users can access deployment config.', 2000);
                    redirectToDashboard();
                    return;
                }
                ServerIntf.ParseError(err, 1800);
            })
            .finally(() => setLoading(false));
    }, []);

    const dirty = useMemo(() => {
        if (!cfg || !form) {
            return false;
        }
        return !sameForm(toForm(cfg), form);
    }, [cfg, form]);

    const stagedDirty = useMemo(() => {
        if (!stagedCfg || !stagedForm) {
            return false;
        }
        const currentRestart = fromLocalDatetimeInput(stagedRestartAtLocal);
        return !sameForm(toForm(stagedCfg), stagedForm) || currentRestart !== stagedCfg.RestartAtMS;
    }, [stagedCfg, stagedForm, stagedRestartAtLocal]);

    if (loading || !cfg || !form) {
        return (
            <div className='container config-page'>
                <div className='config-shell'>
                    <div className='config-card config-card-loading'>Loading deployment config...</div>
                </div>
            </div>
        );
    }

    const onChange = (field, value) => {
        setForm((prev) => ({...prev, [field]: value}));
    };

    const onStagedChange = (field, value) => {
        setStagedForm((prev) => ({...prev, [field]: value}));
    };

    const cancelChanges = () => {
        setForm(toForm(cfg));
    };

    const openProceedModal = () => {
        const payload = editablePayload(form);
        const msg = payloadError(payload);
        if (msg) {
            ServerIntf.ParseError(new Error(msg), 2000);
            return;
        }
        setPendingPayload(payload);
        if (!stagedRestartAtLocal) {
            setStagedRestartAtLocal(toLocalDatetimeInput(Date.now() + 60 * 60 * 1000));
        }
        setModalOpen(true);
    };

    const applyNow = () => {
        if (!pendingPayload) {
            return;
        }
        setSaving(true);
        ServerIntf.UpdateConfig(pendingPayload)
            .then((res) => {
                setCfg(res);
                setForm(toForm(res));
                setModalOpen(false);
                setPendingPayload(null);
                ServerIntf.Success(
                    'Config Updated',
                    'Config saved. Service restart requested immediately.',
                    2500
                );
            })
            .catch((err) => {
                ServerIntf.ParseError(err, 2500);
            })
            .finally(() => setSaving(false));
    };

    const stageFromPending = () => {
        if (!pendingPayload) {
            return;
        }
        const restartAtMS = fromLocalDatetimeInput(stagedRestartAtLocal);
        if (!restartAtMS || restartAtMS <= Date.now()) {
            ServerIntf.ParseError(new Error('Scheduled restart must be a future time'), 2200);
            return;
        }

        setSaving(true);
        ServerIntf.UpsertStagedConfig({...pendingPayload, RestartAtMS: restartAtMS})
            .then((res) => {
                setStagedCfg(res);
                setStagedForm(toForm(res));
                setStagedRestartAtLocal(toLocalDatetimeInput(res.RestartAtMS));
                setForm(toForm(cfg));
                setModalOpen(false);
                setPendingPayload(null);
                ServerIntf.Success('Config Staged', 'Staged config saved and scheduled for restart.', 2500);
            })
            .catch((err) => {
                ServerIntf.ParseError(err, 2500);
            })
            .finally(() => setSaving(false));
    };

    const saveStagedChanges = () => {
        if (!stagedForm || !stagedCfg) {
            return;
        }
        const payload = editablePayload(stagedForm);
        const msg = payloadError(payload);
        if (msg) {
            ServerIntf.ParseError(new Error(msg), 2000);
            return;
        }

        const restartAtMS = fromLocalDatetimeInput(stagedRestartAtLocal);
        if (!restartAtMS || restartAtMS <= Date.now()) {
            ServerIntf.ParseError(new Error('Scheduled restart must be a future time'), 2200);
            return;
        }

        setSaving(true);
        ServerIntf.UpsertStagedConfig({...payload, RestartAtMS: restartAtMS})
            .then((res) => {
                setStagedCfg(res);
                setStagedForm(toForm(res));
                setStagedRestartAtLocal(toLocalDatetimeInput(res.RestartAtMS));
                ServerIntf.Success('Staged Config Updated', 'Pending config has been updated.', 2200);
            })
            .catch((err) => {
                ServerIntf.ParseError(err, 2200);
            })
            .finally(() => setSaving(false));
    };

    const cancelStaged = () => {
        setSaving(true);
        ServerIntf.DeleteStagedConfig()
            .then(() => {
                setStagedCfg(null);
                setStagedForm(null);
                setStagedRestartAtLocal('');
                ServerIntf.Warning('Staged Config Removed', 'Scheduled restart and staged config were canceled.', 2200);
            })
            .catch((err) => {
                ServerIntf.ParseError(err, 2200);
            })
            .finally(() => setSaving(false));
    };

    return (
        <div className='container config-page'>
            <div className='config-shell'>
                <div className='config-title'>
                    <h3>Deployment Config</h3>
                    <p>Runtime configuration for this deployment target.</p>
                </div>

                <div className='config-card'>
                    <div className='config-meta'>
                        <div className='meta-item'>
                            <span className='meta-label'>Version</span>
                            <span className='meta-value'>{cfg.Version}</span>
                        </div>
                        <div className='meta-item'>
                            <span className='meta-label'>SHA</span>
                            <span className='meta-value meta-sha'>{cfg.SHA}</span>
                        </div>
                    </div>

                    {renderConfigFields('cfg', form, onChange)}

                    {dirty && (
                        <div className='config-warning'>
                            <div className='warning-copy'>Modifying configuration can break the current deployment. Proceed only if you are sure.</div>
                            <div className='config-actions'>
                                <button className='btn btn-secondary' onClick={cancelChanges} disabled={saving}>Cancel</button>
                                <button className='btn btn-danger' onClick={openProceedModal} disabled={saving}>
                                    Proceed
                                </button>
                            </div>
                        </div>
                    )}

                    <div className='config-note'>
                        Version and SHA are runtime-managed and read-only.
                    </div>
                </div>

                {stagedCfg && stagedForm && (
                    <div className='config-card staged-card'>
                        <div className='config-title'>
                            <h3>Pending Restart Config</h3>
                            <p>Staged changes to apply on scheduled restart.</p>
                        </div>
                        <div className='config-meta'>
                            <div className='meta-item'>
                                <span className='meta-label'>Scheduled Restart</span>
                                <span className='meta-value'>{new Date(stagedCfg.RestartAtMS).toLocaleString()}</span>
                            </div>
                            <div className='meta-item'>
                                <span className='meta-label'>Restart In</span>
                                <span className='meta-value'>{formatCountdown(stagedCfg.RestartAtMS, nowMS)}</span>
                            </div>
                        </div>

                        {renderConfigFields('staged', stagedForm, onStagedChange)}

                        <div className='config-grid staged-restart-grid'>
                            <label className='config-label' htmlFor='staged-restart-at'>Scheduled Restart</label>
                            <input id='staged-restart-at' type='datetime-local' className='form-control' value={stagedRestartAtLocal}
                                   onChange={(e) => setStagedRestartAtLocal(e.target.value)}/>
                        </div>

                        <div className='config-actions'>
                            <button className='btn btn-secondary' onClick={() => {
                                setStagedForm(toForm(stagedCfg));
                                setStagedRestartAtLocal(toLocalDatetimeInput(stagedCfg.RestartAtMS));
                            }} disabled={saving || !stagedDirty}>Revert</button>
                            <button className='btn btn-primary' onClick={saveStagedChanges} disabled={saving || !stagedDirty}>
                                {saving ? 'Saving...' : 'Save Staged Changes'}
                            </button>
                            <button className='btn btn-danger' onClick={cancelStaged} disabled={saving}>Cancel Scheduled Restart</button>
                        </div>
                    </div>
                )}
            </div>

            {modalOpen && (
                <div className='config-modal-backdrop'>
                    <div className='config-modal'>
                        <h4>Apply Configuration Change</h4>
                        <p>Choose whether to restart immediately or stage these changes for a future restart.</p>

                        <div className='modal-actions'>
                            <button className='btn btn-danger' onClick={applyNow} disabled={saving}>Restart Now</button>
                        </div>

                        <div className='modal-stage'>
                            <label className='config-label' htmlFor='modal-restart-at'>Schedule Restart</label>
                            <input
                                id='modal-restart-at'
                                type='datetime-local'
                                className='form-control'
                                value={stagedRestartAtLocal}
                                onChange={(e) => setStagedRestartAtLocal(e.target.value)}
                            />
                            <button className='btn btn-primary' onClick={stageFromPending} disabled={saving}>Save to Staged</button>
                        </div>

                        <div className='modal-actions'>
                            <button className='btn btn-secondary' onClick={() => setModalOpen(false)} disabled={saving}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
